import React, { useState } from 'react';
import { Box, Button, Typography, Paper, Alert, CircularProgress, TextField } from '@mui/material';
import { getFirestore, doc, setDoc } from 'firebase/firestore';

/**
 * Emergency component to set up admin privileges using a direct user ID
 */
const EmergencyAdminSetup = () => {
  const [userId, setUserId] = useState('ram2PDLyZtNF33al4xO76LLzc9r2'); // Pre-filled with your user ID
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const makeAdmin = async () => {
    if (!userId) {
      setError('User ID is required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const db = getFirestore();
      
      // Directly write to the user document with admin roles
      // This bypasses any read operations which might be causing permission issues
      await setDoc(doc(db, 'users', userId), {
        uid: userId,
        roles: {
          user: true,
          admin: true,
          artist: true,
          superadmin: true
        },
        updatedAt: new Date()
      }, { merge: true });
      
      setSuccess(true);
      console.log('Successfully made user an admin!');
      
    } catch (err) {
      console.error('Error making admin:', err);
      setError(err.message || 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom align="center">
          Emergency Admin Setup
        </Typography>
        
        <Alert severity="info" sx={{ mb: 3 }}>
          This is an emergency tool to directly set admin privileges using your User ID.
          Your User ID is pre-filled based on the console output.
        </Alert>
        
        <TextField
          fullWidth
          label="User ID"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          margin="normal"
          disabled={loading || success}
          sx={{ mb: 2 }}
        />
        
        <Button 
          variant="contained" 
          color="primary"
          fullWidth
          disabled={loading || success || !userId}
          onClick={makeAdmin}
        >
          {loading ? <CircularProgress size={24} /> : 'Make Admin Now'}
        </Button>
        
        {success && (
          <Alert severity="success" sx={{ mt: 3 }}>
            <Typography variant="body1">
              Successfully set admin privileges!
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              You may need to sign out and sign back in for the changes to take effect.
            </Typography>
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Troubleshooting
        </Typography>
        
        <Typography variant="body2" paragraph>
          If you continue to experience permission issues, try these steps:
        </Typography>
        
        <ol>
          <li>Sign out and sign back in after making yourself an admin</li>
          <li>Check the Firebase security rules to ensure they allow write operations</li>
          <li>Try the Create Admin option at <code>/marketplace/create-admin</code> to create a new admin user</li>
          <li>If all else fails, you may need to modify the Firebase security rules directly</li>
        </ol>
      </Paper>
    </Box>
  );
};

export default EmergencyAdminSetup;
